import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  CreateEmailCampaignPayload,
  EmailCampaign,
  EmailCampaignLog,
  EmailCampaignPage,
} from '../types';

/**
 * osprey-api `/email-campaigns` (PE-2125 FE, PE-2124 BFF, PE-891 backend).
 *
 * TCS-staff only (CollectiveOnlyGuard) + `EMAIL_CAMPAIGN.READ`, with
 * `EMAIL_CAMPAIGN.CREATE` additionally required on the POST — all enforced
 * server-side. Platform-level, so NOT tenant-scoped: `/email-campaigns` is absent
 * from `TENANT_SCOPED_PREFIXES` in `shared/utils/fetch.ts` and must stay absent,
 * or every call would grow a `tenantId` query param the BFF doesn't declare. The
 * tenant a campaign targets travels in the POST body instead.
 */
export const emailCampaignsApi = {
  list: async (page = 1, limit = 20): Promise<EmailCampaignPage> => {
    const response = await ApiFetch.get<Enveloped<EmailCampaignPage>>(
      `/email-campaigns?page=${page}&limit=${limit}`,
    );
    return unwrap(response);
  },

  /**
   * Creates AND IMMEDIATELY SENDS. There is no draft state and no scheduling in
   * PE-891, so there is nothing to undo once this resolves — the composer
   * confirms before calling it.
   */
  create: async (payload: CreateEmailCampaignPayload): Promise<EmailCampaign> => {
    const response = await ApiFetch.post<Enveloped<EmailCampaign>>('/email-campaigns', payload);
    return unwrap(response);
  },

  getLogs: async (id: string): Promise<EmailCampaignLog[]> => {
    const response = await ApiFetch.get<Enveloped<EmailCampaignLog[]>>(
      `/email-campaigns/${id}/logs`,
    );
    return unwrap(response);
  },
};
